import { Flex, Progress as ChakraProgress, Stack, Text } from '@chakra-ui/react';
import * as React from 'react';

import { progressLabelGap } from './progress-bar.kds';

const clampProgressValue = (value: number, max: number): number => {
  if (max <= 0 || Number.isNaN(value) || Number.isNaN(max)) {
    return 0;
  }

  const percentage = (value / max) * 100;
  if (Number.isNaN(percentage)) {
    return 0;
  }

  return Math.min(Math.max(percentage, 0), 100);
};

export interface ProgressBarProps extends Omit<ChakraProgress.RootProps, 'size' | 'variant' | 'shape' | 'title'> {
  /** Progress amount. Pass `null` for indeterminate. */
  value?: number | null;
  /** Maximum value used to compute the percentage. Default `100`. */
  max?: number;
  /** Label above the track (Figma title row). */
  title?: React.ReactNode;
  /** Percentage beside the title. Defaults to `true` when `title` is set. */
  showValue?: boolean;
  /** Custom percentage formatter. Receives the clamped 0–100 value. */
  formatValue?: (value: number) => React.ReactNode;
}

export const ProgressBar = React.forwardRef<HTMLDivElement, ProgressBarProps>(function ProgressBar(props, ref) {
  const {
    value = 0,
    max = 100,
    title,
    showValue,
    formatValue = v => `${Math.round(v)}%`,
    children,
    ...rest
  } = props;

  const isIndeterminate = value == null;
  const percentage = isIndeterminate ? 0 : clampProgressValue(value, max);
  const rootValue = isIndeterminate ? null : percentage;
  const shouldShowValue = showValue ?? title != null;
  const titleId = React.useId();

  const progressRoot = (
    <ChakraProgress.Root ref={ref} max={100} value={rootValue} {...rest}>
      <ChakraProgress.Track aria-labelledby={title ? titleId : undefined}>
        <ChakraProgress.Range />
      </ChakraProgress.Track>
      {children}
    </ChakraProgress.Root>
  );

  if (title == null) {
    return progressRoot;
  }

  return (
    <Stack gap={progressLabelGap} width="full">
      <Flex align="center" gap="10" justify="space-between" width="full">
        <Text color="neutral.500" flex="1" id={titleId} minW={0} textStyle="large.regular">
          {title}
        </Text>
        {shouldShowValue && !isIndeterminate && (
          <Text color="neutral.350" flexShrink={0} textStyle="large.medium">
            {formatValue(percentage)}
          </Text>
        )}
      </Flex>
      {progressRoot}
    </Stack>
  );
});
